/**
 * Error Handling Utilities
 * 
 * Centralized error handling and user-friendly error messages
 * - Parse API errors
 * - Format error messages
 * - Log errors for debugging
 * - Announce errors to screen readers
 * 
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 13.5
 */

import { AxiosError } from 'axios';
import { OfflineError } from '../api/rest-client';
import { announceError } from './screenReader';

/**
 * API Error Response structure
 */
export interface ApiErrorResponse {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  statusCode?: number;
}

/**
 * Parsed Error object
 */
export interface ParsedError {
  message: string;
  statusCode?: number;
  fieldErrors?: Record<string, string[]>;
  isNetworkError: boolean;
  isAuthError: boolean;
  isValidationError: boolean;
  isServerError: boolean;
  originalError: Error;
}

/**
 * Parse an error into a user-friendly format
 * Requirements: 12.1, 12.2, 12.3
 */
export function parseError(error: unknown): ParsedError {
  // Handle OfflineError
  if (error instanceof OfflineError) {
    return {
      message: error.message,
      isNetworkError: true,
      isAuthError: false,
      isValidationError: false,
      isServerError: false,
      originalError: error,
    };
  }

  // Handle AxiosError (API errors)
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    
    // Network error (no response)
    if (!axiosError.response) {
      return {
        message: 'Network error. Please check your connection and try again.',
        isNetworkError: true,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
        originalError: axiosError,
      };
    }

    const { status, data } = axiosError.response;

    // Authentication error (401)
    if (status === 401) {
      return {
        message: 'Your session has expired. Please log in again.',
        statusCode: status,
        isNetworkError: false,
        isAuthError: true,
        isValidationError: false,
        isServerError: false,
        originalError: axiosError,
      };
    }

    // Permission error (403)
    if (status === 403) {
      return {
        message: 'Access denied. You do not have permission to perform this action.',
        statusCode: status,
        isNetworkError: false,
        isAuthError: true,
        isValidationError: false,
        isServerError: false,
        originalError: axiosError,
      };
    }

    // Validation error (400)
    if (status === 400) {
      const fieldErrors = data?.errors;
      const message = data?.message || data?.error || 'Invalid request. Please check your input.';
      
      return {
        message,
        statusCode: status,
        fieldErrors,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: true,
        isServerError: false,
        originalError: axiosError,
      };
    }

    // Not found error (404)
    if (status === 404) {
      return {
        message: data?.message || 'The requested resource was not found.',
        statusCode: status,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: false,
        originalError: axiosError,
      };
    }

    // Server error (5xx)
    if (status >= 500) {
      return {
        message: 'Server error. Please try again later.',
        statusCode: status,
        isNetworkError: false,
        isAuthError: false,
        isValidationError: false,
        isServerError: true,
        originalError: axiosError,
      };
    }

    // Other client errors (4xx)
    return {
      message: data?.message || data?.error || 'An error occurred. Please try again.',
      statusCode: status,
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
      isServerError: false,
      originalError: axiosError,
    };
  }

  // Handle generic Error
  if (error instanceof Error) {
    return {
      message: error.message || 'An unexpected error occurred.',
      isNetworkError: false,
      isAuthError: false,
      isValidationError: false,
      isServerError: false,
      originalError: error,
    };
  }

  // Handle unknown error types
  return {
    message: 'An unexpected error occurred.',
    isNetworkError: false,
    isAuthError: false,
    isValidationError: false,
    isServerError: false,
    originalError: new Error(String(error)),
  };
}

/**
 * Log error for debugging
 * Requirements: 12.5
 */
export function logError(error: ParsedError, context?: string): void {
  const prefix = context ? `[${context}]` : '[Error]';
  
  console.error(prefix, {
    message: error.message,
    statusCode: error.statusCode,
    fieldErrors: error.fieldErrors,
    isNetworkError: error.isNetworkError,
    isAuthError: error.isAuthError,
    isValidationError: error.isValidationError,
    isServerError: error.isServerError,
    originalError: error.originalError,
  });

  // In production, you might want to send errors to a logging service
  // e.g., Sentry, LogRocket, CloudWatch, etc.
  if (import.meta.env.PROD) {
    // TODO: Send to error tracking service
    // Example: Sentry.captureException(error.originalError);
  }
}

/**
 * Get user-friendly error message for display
 * Announces error to screen readers
 * Requirements: 12.1, 12.2, 13.5
 */
export function getErrorMessage(error: unknown, announceToSR: boolean = true): string {
  const parsed = parseError(error);
  
  // Announce error to screen readers
  if (announceToSR) {
    announceError(parsed.message);
  }
  
  return parsed.message;
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: ParsedError): boolean {
  return error.isNetworkError || error.isServerError;
}

/**
 * Get field-specific error message
 * Requirements: 12.4
 */
export function getFieldError(
  fieldErrors: Record<string, string[]> | undefined,
  fieldName: string
): string | undefined {
  if (!fieldErrors || !fieldErrors[fieldName]) {
    return undefined;
  }
  
  const errors = fieldErrors[fieldName];
  return errors.length > 0 ? errors[0] : undefined;
}

/**
 * Format validation errors for display
 * Requirements: 12.4
 */
export function formatValidationErrors(
  fieldErrors: Record<string, string[]> | undefined
): string {
  if (!fieldErrors) {
    return 'Validation failed. Please check your input.';
  }

  const messages = Object.entries(fieldErrors)
    .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
    .join('; ');

  return messages || 'Validation failed. Please check your input.';
}
